import { useState, useRef, useEffect } from "react";
import { MapPin, Crosshair, X, ChevronDown } from "lucide-react";
import { useLocation } from "../lib/LocationContext";

export default function LocationPicker() {
  const { city, setCity, supported, requestGeo, detecting } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (c) => {
    setCity(c);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 border border-black/20 hover:border-black px-3 py-2 text-xs font-bold uppercase tracking-widest"
        data-testid="location-picker-toggle"
      >
        <MapPin className="w-3.5 h-3.5 text-[#E63946]" strokeWidth={2} />
        <span className="hidden sm:inline">{city || "Anywhere"}</span>
        <ChevronDown className="w-3 h-3 opacity-60" strokeWidth={2} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 border border-black bg-white z-50 shadow-lg" data-testid="location-picker-menu">
          <div className="p-3 border-b border-black/10">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-1">Deals near</div>
            <div className="font-serif font-bold text-lg">{city || "Anywhere"}</div>
          </div>
          <button
            onClick={async () => { await requestGeo(); setOpen(false); }}
            disabled={detecting}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-50"
            data-testid="location-use-mine"
          >
            <Crosshair className="w-3.5 h-3.5" strokeWidth={2} />
            {detecting ? "Detecting…" : "Use my location"}
          </button>
          <div className="border-t border-black/10">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 px-3 pt-2">Popular cities</div>
            <div className="max-h-56 overflow-y-auto">
              {supported.map((c) => (
                <button
                  key={c}
                  onClick={() => pick(c)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-black/5 ${c === city ? "font-bold text-[#E63946]" : ""}`}
                  data-testid={`location-pick-${c.replace(/\s+/g, "-")}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          {city && (
            <button
              onClick={() => pick("")}
              className="w-full flex items-center justify-center gap-1 px-3 py-2 text-xs font-mono uppercase tracking-widest text-black/50 hover:text-[#E63946] border-t border-black/10"
              data-testid="location-clear"
            >
              <X className="w-3 h-3" strokeWidth={2} /> Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
